import { useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import Home from '../pages/Home';
import DefaultLayout from '../layouts/DefaultLayout.tsx';
import { useTranslation } from 'react-i18next';
import MeetingRoom from '../pages/MeetingRoom.tsx';
import LeaveMeeting from '../pages/LeaveMeeting.tsx';
import Reviews from '../components/Reviews/Reviews.tsx';

import { AuthGaurd } from '../components/auth/AuthGaurd.tsx';
import { ProtectedRoute } from '../components/auth/ProtectedRoute.tsx';
import { Login } from '../components/auth/Login.tsx';
import { ForgotPassword } from '../components/auth/ForgotPassword.tsx';
import { SetupPassword } from '../components/auth/SetupPassword.tsx';
import { VerifyOTP } from '../components/auth/VerifyOTP.tsx';
import { getRouteAuthConfig } from '../constants/routes';
import ProductsPage from '../pages/products/ProductsPage.tsx';
import BiddingPostsPage from '../pages/BiddingPosts/BiddingPostsPage.tsx';
import BiddingPostDetailsPage from '../pages/BiddingPosts/BiddingPostDetailsPage.tsx';
import ProductDetails from '../pages/products/ProductDetails.tsx';
import AdminProductRequestDetails from '../pages/products/AdminProductRequestDetails.tsx';
import CommissionVats from '../pages/AdminConfigurations/commissionVats';
import CoinsServiceLimits from '../pages/AdminConfigurations/coins/index.tsx';
import CategorySetup from '../pages/AdminConfigurations/categories';
import CoinsOffersPage from '../pages/AdminConfigurations/coinsOffers/index.tsx';
import PromotionsRequestsPage from '../pages/AdminConfigurations/promotionsRequests/index.tsx';
import PromotionsSlotsPage from '../pages/AdminConfigurations/promotionsSlots/index.tsx';
import PromotionsDurationsPage from '../pages/AdminConfigurations/promotionsDurations/index.tsx';
import PromotionsReportsPage from '../pages/reports/PromotionsReports/index.tsx';
import SubscriptionsPlansPage from '../pages/AdminConfigurations/subscriptionsPlans/index.tsx';
import EditSubscriptionPlanPage from '../pages/AdminConfigurations/subscriptionsPlans/EditSubscriptionPlanPage.tsx';
import LogsConfigs from '../pages/AdminConfigurations/LogsConfigs.tsx';

import SellersPage from '../pages/Sellers.tsx';
import AddSeller from '../pages/AddSeller';
import SellerProfileCreation from '../pages/SellerProfileCreation';
import YadDefaultPromotionsPage from '../pages/AdminConfigurations/YadDefaultPromotions';
import SellerProfile from '../pages/SellerProfile.tsx';
import AddProduct from '../pages/AddProduct.tsx';
import EditSellerProfile from '../pages/EditSellerProfile.tsx';
import SetupMarketplace from '../pages/SetupMarketplace.tsx';
import DisputeDetails from '@/pages/DisputeDetails.tsx';
import DisputeRequestsPage from '@/pages/Dispute.tsx';
import TotalRevenue from '@/pages/TotalRevenue.tsx';
import OrdersBudget from '@/pages/reports/OrdersBudget.tsx';
import DisputesReports from '@/pages/reports/DisputeReports.tsx';
import OrdersReport from '@/pages/reports/OrdersReport.tsx';
import UsersReport from '@/pages/reports/UsersReport.tsx';
import SubscriptionReports from '@/pages/reports/SubscriptionReports.tsx';
import SupportTicketsPage from '../pages/support-tickets';
import TicketDetailsPage from '../pages/support-tickets/TicketDetailsPage';
import Chat from '../pages/chat/page.tsx';
import LogisticsFeedbackPage from '@/pages/reports/LogisticsFeedback.tsx';

/**
 * Route Authorization Examples:
 *
 * 1. Role-based authorization:
 *    <Route element={<ProtectedRoute requiredRoles={[ADMIN_ROLES.superAdmin, ADMIN_ROLES.admin]} />}>
 *      <Route path="members" element={<Members />} />
 *    </Route>
 *
 * 2. Permission-based authorization (OR logic - user needs ANY of the permissions):
 *    <Route element={<ProtectedRoute requiredPermissions={['VIEW_MEMBERS', 'MANAGE_MEMBERS']} />}>
 *      <Route path="members" element={<Members />} />
 *    </Route>
 *
 * 3. Permission-based authorization (AND logic - user needs ALL permissions):
 *    <Route element={<ProtectedRoute requiredPermissions={['VIEW_MEMBERS', 'MANAGE_MEMBERS']} requireAllPermissions={true} />}>
 *      <Route path="members" element={<Members />} />
 *    </Route>
 *
 * 4. Combined role and permission check:
 *    <Route element={<ProtectedRoute requiredRoles={[ADMIN_ROLES.admin]} requiredPermissions={['VIEW_REPORTS']} />}>
 *      <Route path="reports" element={<Reports />} />
 *    </Route>
 *
 * Note: Super Admin (SUPER_ADMIN) automatically bypasses all permission checks
 */

const Root = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    const dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.dir = dir;
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const homeAuth = getRouteAuthConfig('/');
  const reviewsAuth = getRouteAuthConfig('/reviews');
  const commissionAuth = getRouteAuthConfig('/commission');

  const coinsOffersAuth = getRouteAuthConfig('/coins/offers');
  const coinsServicesAuth = getRouteAuthConfig('/coins/services');
  const disputeAuth = getRouteAuthConfig('/disputes');
  const allCategoriesAuth = getRouteAuthConfig('/categories');
  const promotionsRequestsAuth = getRouteAuthConfig('/promotions-requests');
  const promotionsSlotsAuth = getRouteAuthConfig('/promotions-slots');
  const promotionsDurationsAuth = getRouteAuthConfig(
    '/promotions-durations/home'
  );
  const promotionsReportsAuth = getRouteAuthConfig(
    '/reports/promotions-reports'
  );
  const subscriptionsPlansAuth = getRouteAuthConfig('/subscriptions-plans');
  const supportTicketsAuth = getRouteAuthConfig('/support-tickets');
  const configurationLogsAuth = getRouteAuthConfig('/configuration-logs');

  return (
    <>
      <Routes>
        <Route element={<AuthGaurd />}>
          <Route path="/" element={<DefaultLayout />}>
            {/* Home */}
            <Route
              element={
                <ProtectedRoute
                  requiredRoles={homeAuth?.requiredRoles}
                  requiredPermissions={homeAuth?.requiredPermissions}
                  requireAllPermissions={homeAuth?.requireAllPermissions}
                />
              }
            >
              <Route index element={<Home />} />
            </Route>

            <Route path="/products">
              <Route index element={<ProductsPage />} />
            </Route>

            <Route path="/bidding-posts">
              <Route index element={<BiddingPostsPage />} />
              <Route path=":postId" element={<BiddingPostDetailsPage />} />
            </Route>

            <Route path="/disliked-jobs">
              <Route index element={<BiddingPostsPage />} />
              <Route path=":postId" element={<BiddingPostDetailsPage />} />
            </Route>

            {/* Dispute */}
            <Route
              element={
                <ProtectedRoute
                  requiredRoles={disputeAuth?.requiredRoles}
                  requiredPermissions={disputeAuth?.requiredPermissions}
                  requireAllPermissions={disputeAuth?.requireAllPermissions}
                />
              }
            >
              <Route path="dispute" element={<DisputeRequestsPage />} />
              <Route path="dispute/:disputeId" element={<DisputeDetails />} />
            </Route>

            <Route path="/products/product-details/:id">
              <Route index element={<ProductDetails />} />
            </Route>

            {/* Admin/operator product request review — uses request ID from the sidebar */}
            <Route path="/products/request-details/:requestId">
              <Route index element={<AdminProductRequestDetails />} />
            </Route>

            <Route path="/sellers">
              <Route index element={<SellersPage />} />
              <Route path="addSeller" element={<AddSeller />} />
              <Route
                path=":sellerId/profile-creation"
                element={<SellerProfileCreation />}
              />
              <Route path=":sellerId/profile" element={<SellerProfile />} />
              <Route
                path=":sellerId/profile/add-product"
                element={<AddProduct />}
              />
              <Route
                path=":sellerId/profile/edit"
                element={<EditSellerProfile />}
              />
              <Route
                path=":sellerId/profile/setup-marketplace"
                element={<SetupMarketplace />}
              />
            </Route>

            {/* Reviews */}
            <Route
              element={
                <ProtectedRoute
                  requiredRoles={reviewsAuth?.requiredRoles}
                  requiredPermissions={reviewsAuth?.requiredPermissions}
                  requireAllPermissions={reviewsAuth?.requireAllPermissions}
                />
              }
            >
              <Route path="reviews" element={<Reviews />} />
            </Route>

            {/* Support Tickets */}
            <Route
              element={
                <ProtectedRoute
                  requiredRoles={supportTicketsAuth?.requiredRoles}
                  requiredPermissions={supportTicketsAuth?.requiredPermissions}
                  requireAllPermissions={
                    supportTicketsAuth?.requireAllPermissions
                  }
                />
              }
            >
              <Route path="support-tickets" element={<SupportTicketsPage />} />
              <Route
                path="support-tickets/:id"
                element={<TicketDetailsPage />}
              />
            </Route>

            {/* Chat */}
            <Route>
              <Route path="messages" element={<Chat />} />
            </Route>

            {/* Reports */}
            <Route>
              <Route path="total-revenue" element={<TotalRevenue />} />
            </Route>
            <Route>
              <Route path="orders-budget" element={<OrdersBudget />} />
            </Route>
            <Route>
              <Route path="disputes-reports" element={<DisputesReports />} />
            </Route>
            <Route>
              <Route path="orders-reports" element={<OrdersReport />} />
            </Route>
            <Route>
              <Route path="users-reports" element={<UsersReport />} />
            </Route>
            <Route>
              <Route
                path="subscription-reports"
                element={<SubscriptionReports />}
              />
            </Route>
            <Route>
              <Route
                path="logistics-feedback"
                element={<LogisticsFeedbackPage />}
              />
            </Route>

            {/* Admin Configurations */}
            <Route
              element={
                <ProtectedRoute
                  requiredRoles={commissionAuth?.requiredRoles}
                  requiredPermissions={commissionAuth?.requiredPermissions}
                  requireAllPermissions={commissionAuth?.requireAllPermissions}
                />
              }
            >
              <Route path="commission" element={<CommissionVats />} />
            </Route>
            <Route
              element={
                <ProtectedRoute
                  requiredRoles={coinsServicesAuth?.requiredRoles}
                  requiredPermissions={coinsServicesAuth?.requiredPermissions}
                  requireAllPermissions={
                    coinsServicesAuth?.requireAllPermissions
                  }
                />
              }
            >
              <Route path="coins/services" element={<CoinsServiceLimits />} />
            </Route>
            <Route
              element={
                <ProtectedRoute
                  requiredRoles={allCategoriesAuth?.requiredRoles}
                  requiredPermissions={allCategoriesAuth?.requiredPermissions}
                  requireAllPermissions={
                    allCategoriesAuth?.requireAllPermissions
                  }
                />
              }
            >
              <Route path="categories" element={<CategorySetup />} />
            </Route>
            <Route
              element={
                <ProtectedRoute
                  requiredRoles={coinsOffersAuth?.requiredRoles}
                  requiredPermissions={coinsOffersAuth?.requiredPermissions}
                  requireAllPermissions={coinsOffersAuth?.requireAllPermissions}
                />
              }
            >
              <Route path="coins/offers" element={<CoinsOffersPage />} />
            </Route>
            <Route
              element={
                <ProtectedRoute
                  requiredRoles={promotionsRequestsAuth?.requiredRoles}
                  requiredPermissions={
                    promotionsRequestsAuth?.requiredPermissions
                  }
                  requireAllPermissions={
                    promotionsRequestsAuth?.requireAllPermissions
                  }
                />
              }
            >
              <Route
                path="promotions-requests"
                element={<PromotionsRequestsPage />}
              />
            </Route>
            <Route
              element={
                <ProtectedRoute
                  requiredRoles={promotionsSlotsAuth?.requiredRoles}
                  requiredPermissions={promotionsSlotsAuth?.requiredPermissions}
                  requireAllPermissions={
                    promotionsSlotsAuth?.requireAllPermissions
                  }
                />
              }
            >
              <Route
                path="promotions-slots"
                element={<PromotionsSlotsPage />}
              />
            </Route>
            <Route
              element={
                <ProtectedRoute
                  requiredRoles={promotionsDurationsAuth?.requiredRoles}
                  requiredPermissions={
                    promotionsDurationsAuth?.requiredPermissions
                  }
                  requireAllPermissions={
                    promotionsDurationsAuth?.requireAllPermissions
                  }
                />
              }
            >
              <Route
                path="promotions-durations/home"
                element={<PromotionsDurationsPage />}
              />
              <Route
                path="promotions-durations/category"
                element={<PromotionsDurationsPage />}
              />
            </Route>
            <Route
              element={
                <ProtectedRoute
                  requiredRoles={subscriptionsPlansAuth?.requiredRoles}
                  requiredPermissions={
                    subscriptionsPlansAuth?.requiredPermissions
                  }
                  requireAllPermissions={
                    subscriptionsPlansAuth?.requireAllPermissions
                  }
                />
              }
            >
              <Route
                path="subscriptions-plans"
                element={<SubscriptionsPlansPage />}
              />
              <Route
                path="subscriptions-plans/:planId/edit"
                element={<EditSubscriptionPlanPage />}
              />
            </Route>
            <Route
              path="yad-default-promotions"
              element={<YadDefaultPromotionsPage />}
            />
            <Route
              element={
                <ProtectedRoute
                  requiredRoles={configurationLogsAuth?.requiredRoles}
                  requiredPermissions={
                    configurationLogsAuth?.requiredPermissions
                  }
                  requireAllPermissions={
                    configurationLogsAuth?.requireAllPermissions
                  }
                />
              }
            >
              <Route path="configuration-logs" element={<LogsConfigs />} />
            </Route>
            <Route
              element={
                <ProtectedRoute
                  requiredRoles={promotionsReportsAuth?.requiredRoles}
                  requiredPermissions={
                    promotionsReportsAuth?.requiredPermissions
                  }
                  requireAllPermissions={
                    promotionsReportsAuth?.requireAllPermissions
                  }
                />
              }
            >
              <Route
                path="reports/promotions-reports"
                element={<PromotionsReportsPage />}
              />
            </Route>
          </Route>

          {/* Meeting Room - accessible to all authenticated users */}
          <Route path="meeting-room/:signature" element={<MeetingRoom />} />
          <Route path="leave-room/:disputeId" element={<LeaveMeeting />} />
        </Route>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/verify-otp" element={<VerifyOTP />} />
        <Route path="/setup-password" element={<SetupPassword />} />
      </Routes>
    </>
  );
};

export default Root;
